import React, { useMemo, useRef, useState } from "react";
import { Paper, Typography, Box, type SxProps } from "@mui/material";
import {
  DataGridPro,
  gridClasses,
  type GridColDef,
} from "@mui/x-data-grid-pro";
import { useStore, type Store } from "@tanstack/react-store";
import { useMutationState } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useParams } from "react-router-dom";
import { buildLogsAtomWithId } from "@/atoms";
import DetailModal from "@/routes/recording/detailModal";
import SyntaxHighlighter from "react-syntax-highlighter";
import { stackoverflowDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { DateTime } from "luxon";
import { useHotkey } from "@tanstack/react-hotkeys";
import type { MutableRefObject } from "react";

export interface NormalizedLogRow {
  id: number;
  timestamp: string;
  level?: string;
  message: string;
  process?: string;
  relativeTimestamp?: number | null;
}

const columns: GridColDef[] = [
  {
    field: "timestamp",
    headerName: "Time",
    width: 100,
    sortable: false,
  },
  {
    field: "process",
    headerName: "Process",
    width: 100,
    sortable: false,
  },
  {
    field: "level",
    headerName: "Level",
    width: 100,
    sortable: false,
  },
  {
    field: "message",
    headerName: "Message",
    flex: 1,
    sortable: false,
  },
];

const gridSx = {
  height: "100%",
  backgroundColor: "#1e1e1e",
  border: "none",
  borderRadius: 1,
  "& .MuiDataGrid-cell": {
    fontFamily: "monospace",
    fontSize: 12,
    borderBottom: "1px solid #333",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#1e1e1e",
    borderBottom: "1px solid #333",
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
      fontSize: 12,
      color: "#90caf9",
      textTransform: "uppercase",
    },
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
  },
  "& .row-current": { borderBottom: "3px solid #90caf9 !important" },
  "& .MuiDataGrid-virtualScroller": {
    "&::-webkit-scrollbar": {
      width: 8,
      height: 8,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#555",
      borderRadius: 4,
    },
  },
  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
    outline: "transparent",
  },
  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
    {
      outline: "none",
    },
  [`& .${gridClasses.row}:hover`]: {
    cursor: "pointer",
  },
};

const detailGridSx = {
  border: "none",
  "& .MuiDataGrid-cell": {
    fontFamily: "monospace",
    fontSize: 12,
    alignItems: "flex-start",
    paddingTop: "8px",
    paddingBottom: "8px",
    borderBottom: "1px solid #333",
  },
  "& .MuiDataGrid-columnHeaders": { display: "none" },
};

const detailNameColumn: GridColDef = {
  field: "name",
  headerName: "Field",
  width: 90,
  sortable: false,
  renderCell: (params) => (
    <Box
      sx={{
        fontWeight: 600,
        fontSize: 12,
        color: "#90caf9",
        fontFamily: "monospace",
        textTransform: "uppercase",
      }}
    >
      {params.value}
    </Box>
  ),
};

function DeviceLogDetail({ log }: { log: NormalizedLogRow }) {
  const parsedJson = useMemo(() => {
    try {
      const parsed = JSON.parse(log.message);
      // Only treat as JSON if it's an object or array, not a plain primitive
      if (parsed !== null && typeof parsed === "object") return parsed;
      return null;
    } catch {
      return null;
    }
  }, [log.message]);

  const rows = [
    { id: 0, name: "Time", value: log.timestamp },
    ...(log.level ? [{ id: 1, name: "Level", value: log.level }] : []),
    ...(log.process ? [{ id: 2, name: "Process", value: log.process }] : []),
    { id: 3, name: "Message", value: log.message },
  ];

  const valueColumn: GridColDef = useMemo(
    () => ({
      field: "value",
      headerName: "Value",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (params.row.name === "Message" && parsedJson !== null) {
          return (
            <SyntaxHighlighter
              language="json"
              style={stackoverflowDark}
              customStyle={{ margin: 0, width: "100%", fontSize: 12 }}
            >
              {JSON.stringify(parsedJson, null, 2)}
            </SyntaxHighlighter>
          );
        }
        return (
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              width: "100%",
              fontFamily: "monospace",
              fontSize: 12,
            }}
          >
            {params.value}
          </Box>
        );
      },
    }),
    [parsedJson]
  );

  return (
    <DataGridPro
      rows={rows}
      columns={[detailNameColumn, valueColumn]}
      hideFooter
      disableColumnMenu
      disableRowSelectionOnClick
      getRowHeight={() => "auto"}
      autoHeight
      sx={detailGridSx}
    />
  );
}

export interface DeviceLogsGridProps {
  rows: NormalizedLogRow[];
  loading?: boolean;
  /** When provided, enables playback-sync row highlighting */
  playbackTimestamp?: number;
  currentTimestampIndex?: number;
  apiRef?: MutableRefObject<any>;
  /** Overrides the outer container Box sx. Defaults to a fixed calc(100vh-300px) height. */
  containerSx?: SxProps;
  /**
   * When true, registers a global Mod+F hotkey that focuses the DataGrid
   * quick filter (overriding the browser's native Find). Defaults to true.
   */
  enableQuickFilterHotkey?: boolean;
}

export function DeviceLogsGrid({
  rows,
  loading = false,
  playbackTimestamp,
  currentTimestampIndex,
  apiRef,
  containerSx,
  enableQuickFilterHotkey = true,
}: DeviceLogsGridProps) {
  const [selectedLog, setSelectedLog] = useState<NormalizedLogRow | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPlaybackMode =
    playbackTimestamp !== undefined && currentTimestampIndex !== undefined;

  useHotkey(
    "Mod+F",
    () => {
      const container = containerRef.current;
      if (!container) return;

      const input = container.querySelector(
        '[class*="MuiDataGrid-toolbarQuickFilter"] input'
      ) as HTMLElement;

      if (document.activeElement === input) return;

      (
        container.querySelector(
          '[class*="MuiDataGrid-toolbarQuickFilterTrigger"]'
        ) as HTMLElement
      )?.click();
    },
    { preventDefault: enableQuickFilterHotkey, enabled: enableQuickFilterHotkey }
  );

  if (!loading && rows.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="text.secondary" align="center">
          No device logs yet. Logs will appear here when the session is running.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <DetailModal
        open={!!selectedLog}
        handleClose={() => {
          setSelectedLog(null);
        }}
        title="Device Log Details"
      >
        {selectedLog ? <DeviceLogDetail log={selectedLog} /> : <></>}
      </DetailModal>
      <Box
        ref={containerRef}
        sx={
          containerSx ?? {
            height: "calc(100vh - 300px)",
            minHeight: 400,
            width: "100%",
          }
        }
      >
        <DataGridPro
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          loading={loading}
          density="compact"
          hideFooter
          showToolbar={true}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnMenu
          slotProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
          }}
          disableRowSelectionOnClick
          onRowClick={(params) => {
            setSelectedLog(params.row as NormalizedLogRow);
          }}
          getRowClassName={(params) => {
            if (isPlaybackMode) {
              const { relativeTimestamp } = params.row;
              if (relativeTimestamp == null) return "";
              if (params.row.id === currentTimestampIndex) return "row-current";
              if (relativeTimestamp <= playbackTimestamp) return "row-before";
              return "row-after";
            }
            return "";
          }}
          sx={gridSx}
        />
      </Box>
    </>
  );
}

function SessionLogsContent({
  logsStore,
}: {
  logsStore: Store<NormalizedLogRow[]>;
}) {
  const rows = useStore(logsStore, (state) => state);
  return <DeviceLogsGrid rows={rows} />;
}

export default function DeviceLogs() {
  const { buildId } = useParams();
  const buildLogs = useAtomValue(buildLogsAtomWithId(buildId));

  const mutationData = useMutationState({
    filters: { mutationKey: ["createSessionMutation"], status: "success" },
    select: (mutation) => mutation.state.data,
  });

  const sessionData = mutationData.at(-1);
  const logsStore = (sessionData as any)?.getLogsStore?.();

  // Prefer atom logs (richer data with timestamps) when available
  if (buildLogs.logs.length > 0) {
    const rows: NormalizedLogRow[] = buildLogs.logs.map((log, index) => {
      const separatorIndex = log.message.indexOf(": ");
      const source =
        separatorIndex !== -1
          ? log.message.slice(0, separatorIndex)
          : undefined;
      const message =
        separatorIndex !== -1
          ? log.message.slice(separatorIndex + 2)
          : log.message;
      return {
        id: index,
        timestamp: log.absoluteTimestamp
          ? DateTime.fromMillis(log.absoluteTimestamp).toFormat("hh:mm:ss")
          : "--:--:--",
        message,
        process: source,
        level: "System",
      };
    });

    return <DeviceLogsGrid rows={rows} />;
  }

  // Fall back to Appetize clone session store
  if (logsStore) {
    return <SessionLogsContent logsStore={logsStore} />;
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography color="text.secondary" align="center">
        No device logs yet. Logs will appear here when the session is running.
      </Typography>
    </Paper>
  );
}
